import { useCallback, useEffect, useState } from "react";
import {
  ADMIN_ROLES,
  COACH_MEMORY_CATEGORY_LABELS,
  PATIENT_MEMORY_CORRECTION_MAX_CHARS,
  PATIENT_MEMORY_REQUEST_CHANNELS,
  PATIENT_MEMORY_REQUEST_CHANNEL_LABELS,
  PATIENT_MEMORY_REQUEST_KIND_LABELS,
  PATIENT_MEMORY_REQUEST_KINDS,
  type CoachMemoryCategory,
  type CoachMemoryRequestDto,
  type PatientMemoryRequestKind,
  type RegisterCoachMemoryRequest,
  type ResolveCoachMemoryRequest,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import {
  CoachDraftsDisabledError,
  listMemoryRequests,
  registerMemoryRequest,
  resolveMemoryRequest,
} from "@/lib/coach-draft-api";
import { ForbiddenError, UnauthorizedError } from "@/lib/clinician-api";
import { errorText } from "@/lib/error-text";
import { hiddenWhenReadOnly } from "./SupportView";

// TES-1237 S3, TES-1367 — the patient's requests about her Patientminne
// (A-01 §9), in the coach's Patientminne panel.
//
// TES-1367 (Emelie, Ö-462): the patient has no view of Patientminnet. She
// asks by mail, chat or phone, and her coach registers the request on an
// entry ("Registrera begäran"). While a request is open the entry is kept
// out of every AI draft. The coach then corrects the entry with the panel's
// own "Rätta" and marks the request done, erases the entry on a deletion
// request, restricts it on a restriction request (kept, but no longer
// shown or used), or declines with a note.

const BTN =
  "rounded-md border border-surfaceMuted px-sm py-xs text-xs text-textPrimary hover:bg-surfaceMuted disabled:opacity-50";
const FIELD =
  "rounded-md border border-surfaceMuted bg-surface p-xs text-xs text-textPrimary";

// The one action that carries out each kind of request.
const CARRY_OUT: Record<
  PatientMemoryRequestKind,
  { action: ResolveCoachMemoryRequest["action"]; label: string }
> = {
  correction: { action: "resolved", label: "Markera som rättad" },
  deletion: { action: "erase", label: "Radera uppgiften" },
  restriction: { action: "restrict", label: "Begränsa uppgiften" },
};

function PatientMemoryRequestsPanel({
  patientId,
  onChanged,
  onUnauthorized,
}: {
  patientId: string;
  onChanged: () => void;
  onUnauthorized: () => void;
}) {
  const [rows, setRows] = useState<CoachMemoryRequestDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [declining, setDeclining] = useState<string | null>(null);
  const [note, setNote] = useState("");

  const fail = useCallback(
    (e: unknown) => {
      if (e instanceof UnauthorizedError) onUnauthorized();
      else
        setError(e instanceof Error ? errorText(e.message) : "Något gick fel.");
    },
    [onUnauthorized],
  );

  useEffect(() => {
    let cancelled = false;
    listMemoryRequests(patientId)
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (
          e instanceof CoachDraftsDisabledError ||
          e instanceof ForbiddenError
        ) {
          setRows(null);
          return;
        }
        fail(e);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId, fail]);

  const open = (rows ?? []).filter((r) => r.status === "open");
  if (open.length === 0 && !error) return null;

  async function act(id: string, action: ResolveCoachMemoryRequest["action"]) {
    setBusy(id);
    setError(null);
    try {
      setRows(
        await resolveMemoryRequest(patientId, id, {
          action,
          ...(action === "declined" ? { note: note.trim() } : {}),
        }),
      );
      setDeclining(null);
      setNote("");
      onChanged();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="rounded-md border border-warning p-sm flex flex-col gap-sm text-sm">
      <h3 className="font-medium">Patientens begäran om Patientminnet</h3>
      <p className="text-xs text-textSecondary">
        Uppgiften används inte i AI-förslag så länge begäran är öppen.
      </p>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      {open.map((r) => (
        <div
          key={r.id}
          className="flex flex-col gap-xs border-t border-surfaceMuted pt-xs"
        >
          <p className="text-xs text-textSecondary">
            Begär {PATIENT_MEMORY_REQUEST_KIND_LABELS[r.kind].toLowerCase()} ·{" "}
            {COACH_MEMORY_CATEGORY_LABELS[
              r.itemCategory as CoachMemoryCategory
            ] ?? r.itemCategory}
          </p>
          {r.itemBody ? (
            <p>{r.itemBody}</p>
          ) : (
            <p className="text-textSecondary">Texten visas inte.</p>
          )}
          {/* Her description of the request, for any kind (Ö-605). */}
          {r.body ? <p className="text-xs">{r.body}</p> : null}
          {declining === r.id ? (
            <div className="flex flex-col gap-xs">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                aria-label="Varför avböjs begäran?"
                placeholder="Varför avböjs begäran? Obligatoriskt."
                className={`w-full ${FIELD}`}
              />
              <div className="flex gap-xs">
                <button
                  type="button"
                  onClick={() => void act(r.id, "declined")}
                  disabled={busy !== null || !note.trim()}
                  className={BTN}
                >
                  Bekräfta avböjande
                </button>
                <button
                  type="button"
                  onClick={() => setDeclining(null)}
                  className={BTN}
                >
                  Avbryt
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-xs">
              <button
                type="button"
                onClick={() => void act(r.id, CARRY_OUT[r.kind].action)}
                disabled={busy !== null}
                className={BTN}
              >
                {CARRY_OUT[r.kind].label}
              </button>
              <button
                type="button"
                onClick={() => setDeclining(r.id)}
                disabled={busy !== null}
                className={BTN}
              >
                Avböj
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Hidden in the admin's read-only support view: it resolves and erases.
export const PatientMemoryRequests = hiddenWhenReadOnly(
  PatientMemoryRequestsPanel,
);

// TES-1367 — "Registrera begäran" on one entry: the coach records a request
// the patient made outside the app. The admin does too, in her support view
// (Simon 2026-10-15): a request often reaches Ella through support. Coach
// and admin only, like the route; the answer carries no memory content.
function RegisterMemoryRequestForm({
  patientId,
  itemId,
  onRegistered,
  onUnauthorized,
}: {
  patientId: string;
  itemId: string;
  onRegistered: () => void;
  onUnauthorized: () => void;
}) {
  const { claims } = useAuth();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<PatientMemoryRequestKind>("correction");
  const [receivedVia, setReceivedVia] =
    useState<RegisterCoachMemoryRequest["receivedVia"]>("email");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const role = claims?.role ?? "";
  if (role !== "coach" && !(ADMIN_ROLES as readonly string[]).includes(role)) {
    return null;
  }
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className={BTN}>
        Registrera begäran
      </button>
    );
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await registerMemoryRequest(patientId, {
        itemId,
        kind,
        receivedVia,
        text: text.trim(),
      });
      setOpen(false);
      setText("");
      onRegistered();
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorized();
      else
        setError(e instanceof Error ? errorText(e.message) : "Något gick fel.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-xs flex w-full flex-col gap-xs rounded-md border border-surfaceMuted p-xs text-xs">
      {/* Words: Ö-605, Emelie 2026-10-21, verbatim. */}
      <p className="text-textSecondary">
        Registrera patientens begäran och hur den kom in.
      </p>
      <div className="flex flex-wrap gap-sm">
        <label className="flex items-center gap-xs">
          Typ
          <select
            value={kind}
            onChange={(e) =>
              setKind(e.target.value as PatientMemoryRequestKind)
            }
            className={FIELD}
          >
            {PATIENT_MEMORY_REQUEST_KINDS.map((k) => (
              <option key={k} value={k}>
                {PATIENT_MEMORY_REQUEST_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-xs">
          Kanal
          <select
            value={receivedVia}
            onChange={(e) =>
              setReceivedVia(
                e.target.value as RegisterCoachMemoryRequest["receivedVia"],
              )
            }
            className={FIELD}
          >
            {PATIENT_MEMORY_REQUEST_CHANNELS.map((c) => (
              <option key={c} value={c}>
                {PATIENT_MEMORY_REQUEST_CHANNEL_LABELS[c]}
              </option>
            ))}
          </select>
        </label>
      </div>
      {/* One required field for every kind (Ö-605). */}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        aria-label="Beskriv patientens begäran"
        placeholder="Beskriv patientens begäran"
        maxLength={PATIENT_MEMORY_CORRECTION_MAX_CHARS}
        required
        className={`w-full ${FIELD}`}
      />
      {error ? <p className="text-danger">{error}</p> : null}
      <div className="flex gap-xs">
        <button
          type="button"
          onClick={() => void save()}
          disabled={busy || !text.trim()}
          className={BTN}
        >
          Spara begäran
        </button>
        <button type="button" onClick={() => setOpen(false)} className={BTN}>
          Avbryt
        </button>
      </div>
    </div>
  );
}

// Not hidden in the admin's support view: registering a request is the one
// write she has on Patientminnet (admin-read-only.spec.ts, TES-1367).
export const RegisterMemoryRequest = RegisterMemoryRequestForm;
